import { CookieType } from "../helpers/enums";
import { isSameMonth, format } from "date-fns";
import { enGB, fr, de, it, es } from "date-fns/locale";
import Swiper, { FreeMode, Navigation, Pagination } from "swiper";
import { breakpoints } from "../helpers/breakpoints";
import { determineBrandFromURL, BRANDS } from "../helpers/brand";

export const savedSearches = async () => {
  const brand = determineBrandFromURL();

  const contentURL = window["contentURL"];

  const isStaycity = brand === BRANDS.STAYCITY;
  const cookieToUse = isStaycity
    ? CookieType.SEARCH_STAYCITY
    : CookieType.SEARCH_WILDE;
  const availabilityCookie = document.cookie.match(
    new RegExp(`(^| )${cookieToUse}=([^;]+)`)
  );
  const languageCookie = document.cookie.match(
    new RegExp(`(^| )${CookieType.LANGUAGE}=([^;]+)`)
  );

  const language = languageCookie?.[2].toLowerCase();

  const componentContainer = document.querySelector<HTMLElement>(
    'div[data-content-element-type-alias="savedSearches"]'
  );

  const containers =
    document.querySelectorAll<HTMLElement>(`[js-saved-searches]`);

  const contentApiUrl = ({
    type,
    brand,
    language,
  }: {
    type: string;
    brand: string | null;
    language: string;
  }) => {
    const params: string[] = [];
    if (language) {
      params.push(`language=${language}`);
    }
    if (type) {
      params.push(`contenttype=${type}`);
    }
    if (brand) {
      params.push(`brand=${brand}`);
    }
    return `${contentURL}?${params.join("&")}`;
  };

  async function getDataAndMap() {
    if (language) {
      const generalData = contentApiUrl({ type: "General", brand, language });
      const cityData = contentApiUrl({
        type: "City",
        brand: brand === BRANDS.STAYCITY ? null : BRANDS.WILDE,
        language,
      });

      const responses = await Promise.all([
        fetch(generalData),
        fetch(cityData),
      ]);

      const generalDataJson = await responses[0].json();
      const cityDataJson = await responses[1].json();

      if (generalDataJson && cityDataJson) {
        const generalContent = generalDataJson.map(({ content }) =>
          JSON.parse(content)
        );

        const cityContent = cityDataJson.map(({ content, owscode }) => ({
          owscode,
          ...JSON.parse(content),
        }));

        containers.length > 0 && availabilityCookie
          ? containers.forEach((savedSearchSection) => {
              const parsedSearches = JSON.parse(
                decodeURIComponent(availabilityCookie[2])
              );

              const savedSearchSectionSwipperWrapper =
                document.createElement("div");
              savedSearchSectionSwipperWrapper.classList.add("swiper-wrapper");
              savedSearchSection.appendChild(savedSearchSectionSwipperWrapper);

              parsedSearches.forEach((search) => {
                if (!search.arrivalDate && !search.departureDate) {
                  const searchItemWrapper = document.createElement("div");

                  let cityByCityCode: any = null;
                  let hotelByHotelCode: any = null;

                  cityContent.map((city) => {
                    if (search.hotelCode) {
                      const hotelContent = city.Hotels.find(
                        ({ hotelCode }) => hotelCode == search.hotelCode
                      );
                      if (hotelContent) {
                        hotelByHotelCode = hotelContent;
                      }
                    }
                    if (search.cityCode && city.owscode == search.cityCode) {
                      cityByCityCode = city;
                    }
                  });

                  if (cityByCityCode) {
                    searchItemWrapper.classList.add(
                      "padding-20",
                      "flex",
                      "align-items--center",
                      "flex--nowrap",
                      "border--round-sm",
                      "flex-shrink--0",
                      "h--100"
                    );

                    searchItemWrapper.style.backgroundColor = isStaycity
                      ? "#FCF2E1"
                      : "#EBE0CE";

                    const searchItemContinueButton =
                      document.createElement("button");
                    searchItemContinueButton.classList.add(
                      "t-align--left",
                      "cursor--pointer",
                      "w-100",
                      "flex",
                      "flex-wrap",
                      "flex-dir--column"
                    );
                    searchItemContinueButton.style.color = isStaycity
                      ? "#2e2739"
                      : "#000000";
                    const searchItemContinueButtonText =
                      document.createElement("div");
                    const searchItemContinueButtonTextTop =
                      document.createElement("div");

                    const textNodes = [cityByCityCode?.CityName];

                    if (hotelByHotelCode?.hotelNameShortSearchbar) {
                      textNodes.push(hotelByHotelCode?.hotelNameShortSearchbar);
                    } else if (hotelByHotelCode?.hotelName) {
                      textNodes.push(hotelByHotelCode?.hotelName);
                    }

                    searchItemContinueButtonTextTop.appendChild(
                      document.createTextNode(`${textNodes.join(", ")}`)
                    );
                    searchItemContinueButtonTextTop.classList.add(
                      isStaycity ? "t-tags-caps" : "t-copy-small",
                      "color--current-color"
                    );

                    const getLocale = (language) => {
                      switch (language) {
                        case "fr":
                          return fr;
                        case "de":
                          return de;
                        case "it":
                          return it;
                        case "es":
                          return es;
                        default:
                          return enGB;
                      }
                    };

                    const datesText = () => {
                      const arrivalDateParts = search.arrival.split("-");
                      const departureDateParts = search.departure.split("-");

                      const arrivalDateSplit = new Date(
                        arrivalDateParts[0],
                        arrivalDateParts[1] - 1,
                        arrivalDateParts[2]
                      );

                      const departureDateSplit = new Date(
                        departureDateParts[0],
                        departureDateParts[1] - 1,
                        departureDateParts[2]
                      );

                      const datesAreInSameMonth = isSameMonth(
                        arrivalDateSplit,
                        departureDateSplit
                      );

                      const arrival = format(
                        arrivalDateSplit,
                        datesAreInSameMonth ? "d" : "d LLL",
                        { locale: getLocale(language) }
                      );

                      const departure = format(departureDateSplit, "d LLL", {
                        locale: getLocale(language),
                      });

                      return `${arrival}-${departure}`;
                    };

                    const guestText =
                      search.guests > 1
                        ? `${generalContent[0].guestPluralText}`
                        : `${generalContent[0].guestText}`;

                    const searchItemContinueButtonTextBottom =
                      document.createElement("div");
                    searchItemContinueButtonTextBottom.appendChild(
                      document.createTextNode(
                        `${datesText()}, ${search.guests} ${guestText}`
                      )
                    );
                    searchItemContinueButtonTextBottom.classList.add(
                      "t-copy-tiny",
                      "opacity--7",
                      "color--current-color"
                    );

                    searchItemContinueButtonText.append(
                      searchItemContinueButtonTextTop,
                      searchItemContinueButtonTextBottom
                    );
                    searchItemContinueButton.appendChild(
                      searchItemContinueButtonText
                    );
                    searchItemContinueButton.addEventListener("click", () => {
                      location.href = `${search.url}&resumeSearchId=${search.time}`;
                    });

                    const searchItemRemoveButton =
                      document.createElement("button");
                    searchItemRemoveButton.style.color = isStaycity
                      ? "#2e2739"
                      : "#000000";
                    const searchItemRemoveButtonIcon = document.createElementNS(
                      "http://www.w3.org/2000/svg",
                      "svg"
                    );
                    searchItemRemoveButtonIcon.classList.add(
                      "icon--xs",
                      "color--current-color"
                    );
                    const searchItemRemoveButtonIconToUse =
                      document.createElementNS(
                        "http://www.w3.org/2000/svg",
                        "use"
                      );
                    searchItemRemoveButtonIconToUse.setAttributeNS(
                      "http://www.w3.org/1999/xlink",
                      "xlink:href",
                      "/dist/assets/icons/sprite.svg#cross"
                    );
                    searchItemRemoveButtonIcon.appendChild(
                      searchItemRemoveButtonIconToUse
                    );

                    searchItemRemoveButton.classList.add(
                      "flex-shrink--0",
                      "padding-20--l",
                      "margin-20--l",
                      "border--left",
                      "cursor--pointer",
                      "h--100"
                    );
                    searchItemRemoveButton.appendChild(
                      searchItemRemoveButtonIcon
                    );
                    searchItemRemoveButton.addEventListener("click", () => {
                      const removeItem = parsedSearches.findIndex(
                        (item) => item.time === search.time
                      );
                      searchItemWrapper?.parentElement?.classList.add(
                        "hidden--important"
                      );

                      parsedSearches.splice(removeItem, 1);

                      if (parsedSearches?.length === 0 && componentContainer) {
                        componentContainer.style.display = "none";
                      }

                      const expires = new Date();
                      expires.setTime(
                        expires.getTime() + 30 * 24 * 60 * 60 * 1000
                      );
                      document.cookie = `${cookieToUse}=${JSON.stringify(
                        parsedSearches
                      )}; path=/; samesite=lax; domain=${window.location.hostname
                        .split(".")
                        .slice(-2)
                        .join(
                          "."
                        )}; expires=${expires.toUTCString()}; max-age=2592000;`;
                    });

                    searchItemWrapper.appendChild(searchItemContinueButton);
                    searchItemWrapper.appendChild(searchItemRemoveButton);

                    const searchItemWrapperSlide =
                      document.createElement("div");
                    searchItemWrapperSlide.classList.add(
                      "swiper-slide",
                      "w--fit-content--important"
                    );
                    searchItemWrapperSlide.appendChild(searchItemWrapper);

                    savedSearchSectionSwipperWrapper.append(
                      searchItemWrapperSlide
                    );

                    if (componentContainer) {
                      componentContainer.style.display = "block";

                      const pagination = {
                        el: `.swiper-pagination-${savedSearchSection.id}`,
                        dynamicBullets: true,
                        dynamicMainBullets: 3,
                        clickable: true,
                        bulletElement: "button",
                      };

                      const swiperParams = {
                        modules: [FreeMode, Navigation, Pagination],
                        pagination,
                        freeMode: {
                          freeMode: { enabled: true, sticky: true },
                        },
                        navigation: {
                          nextEl: `.swiper-button-next-${savedSearchSection.id}`,
                          prevEl: `.swiper-button-prev-${savedSearchSection.id}`,
                        },
                        slidesPerView: "auto",
                        spaceBetween: 13,
                        breakpoints: {
                          [breakpoints.screen1024]: {
                            slidesPerView: "auto",
                            spaceBetween: 34,
                          },
                        },
                      };

                      new Swiper(`#${savedSearchSection.id}`, swiperParams);
                    }
                  }
                }
              });
            })
          : null;
      }
    }
  }

  if (contentURL) {
    getDataAndMap();
  }
};
